/**
 * MONATSPAUSCHALE — Anlage-Regeln fuer Katalog und Projekt-Wizard.
 *
 * flatRateLogic beantwortet, was eine BESTEHENDE Pauschale deckt und welche
 * Monate offen sind. Dieses Modul beantwortet die Fragen DAVOR: wie wird aus
 * einer Position mit Einheit 'Monat' ueberhaupt eine wirksame Pauschale, wann
 * ist die Eingabe unvollstaendig, und welcher Deckungsumfang ist in welchem
 * Kontext sinnvoll. Getrennt von flatRateLogic, damit die Geldpfade
 * (invoiceLogic/projectLv) nie an Formular-Regeln haengen — sie importieren
 * flatRateLogic, die Dialoge importieren zusaetzlich dieses Modul.
 *
 * KERNREGEL DER ANLAGE (Betreiber-Entscheidung 4 sinngemaess): die Einheit
 * 'Monat' allein erfindet NIE eine Deckung. Ohne ausdruecklich gewaehlten
 * Umfang entsteht keine flatRate — eine still erzeugte Deckung wuerde je nach
 * scope ungefragt Geld stilllegen. Deshalb gibt es hier keine Funktion, die
 * aus einer Einheit eine flatRate ableitet: jede Deckung entsteht nur ueber
 * applyFlatScopeChoice mit einer echten Auswahl.
 */

import type { FlatRateCoverage, ServiceAssignment } from '../types';
import { isMonthlyUnit } from './serviceUnits';
import {
    FlatCoverageServiceLike,
    isFlatRateService,
    resolveFlatCoverage,
} from './flatRateLogic';

/** Die Position, wie die Anlage-Dialoge sie sehen — Deckung plus Einheit. */
export interface FlatSetupServiceLike extends FlatCoverageServiceLike {
    unit?: string;
}

/** Leere Auswahl = "keine Pauschale". Bewusst kein eigener 'none'-Wert im Datenmodell. */
export type FlatScopeChoice = '' | FlatRateCoverage['scope'];

/**
 * Anzeigetexte der Umfaenge — EINMAL definiert, damit Katalog-Editor und
 * Wizard dieselben Worte benutzen und niemand "alle Positionen" fuer etwas
 * anderes haelt als der Nachbar-Dialog.
 */
export const FLAT_SCOPE_LABELS: Record<FlatRateCoverage['scope'], string> = {
    inspection: 'nur Begehungen',
    all: 'alle Positionen',
    explicit: 'einzelne Positionen',
};

/**
 * Im KATALOG (Stamm-Katalog und Vorlagen) sind nur die regelbasierten
 * Umfaenge waehlbar: 'explicit' benennt konkrete Positions-Ids, und die gibt
 * es erst im Projekt — ein Katalogeintrag mit 'explicit' truege Ids, die in
 * jedem Projekt ins Leere zeigen (resolveFlatCoverage laesst geloeschte Ids
 * fallen, die Pauschale deckte still nichts).
 */
export const CATALOG_FLAT_SCOPES: ReadonlyArray<FlatRateCoverage['scope']> = ['inspection', 'all'];

/** Im PROJEKT sind alle drei Umfaenge waehlbar. */
export const PROJECT_FLAT_SCOPES: ReadonlyArray<FlatRateCoverage['scope']> = [
    'inspection',
    'all',
    'explicit',
];

/** Der aktuell gewaehlte Umfang einer Position, '' fuer "keine Pauschale". */
export const flatScopeChoiceOf = (s: {
    flatRate?: FlatRateCoverage;
}): FlatScopeChoice => (isFlatRateService(s as FlatCoverageServiceLike) ? s.flatRate!.scope : '');

/**
 * Das Feldpaar, das eine Umfangs-Auswahl an der Position aendert. Immer BEIDE
 * Felder zusammen: assignment 'flatrate' ohne flatRate ist genau die halb
 * angelegte Pauschale, die nichts deckt (der Codex-Befund dieser Runde), und
 * eine flatRate ohne assignment fehlte in jeder Anzeige und Sortierung.
 */
export interface FlatSetupPatch {
    assignment: ServiceAssignment;
    flatRate: FlatRateCoverage | undefined;
}

/**
 * Rechnet eine Auswahl im Deckungs-Select in das Feldpaar um.
 *
 *  - '' nimmt die Pauschale zurueck: flatRate faellt weg, und das assignment
 *    faellt nur dann auf 'none' zurueck, wenn es 'flatrate' war — eine
 *    Position, die aus anderem Grund z. B. 'inspection' traegt, behaelt ihre
 *    Zuordnung.
 *  - 'explicit' uebernimmt die uebergebenen Ids bzw. behaelt die schon
 *    gewaehlten; ohne beides beginnt die Liste leer und die Eingabe ist
 *    UNVOLLSTAENDIG (flatRateSetupIssues meldet das) — hier still Ids zu
 *    erfinden waere derselbe Automatismus, den es nicht geben darf.
 */
export const applyFlatScopeChoice = (
    current: { assignment?: ServiceAssignment; flatRate?: FlatRateCoverage },
    choice: FlatScopeChoice,
    serviceIds?: string[],
): FlatSetupPatch => {
    if (choice === '') {
        return {
            assignment: current.assignment === 'flatrate' ? 'none' : current.assignment ?? 'none',
            flatRate: undefined,
        };
    }
    if (choice === 'explicit') {
        return {
            assignment: 'flatrate',
            flatRate: {
                scope: 'explicit',
                serviceIds: serviceIds ?? current.flatRate?.serviceIds ?? [],
            },
        };
    }
    return { assignment: 'flatrate', flatRate: { scope: choice } };
};

/**
 * Haken an/aus fuer EINE Position in der 'explicit'-Liste. Ergebnis ist immer
 * scope 'explicit' — die Funktion wird nur aus der Id-Auswahl aufgerufen, und
 * die gibt es nur dort.
 */
export const toggleExplicitServiceId = (
    current: { assignment?: ServiceAssignment; flatRate?: FlatRateCoverage },
    serviceId: string,
): FlatSetupPatch => {
    const ids = current.flatRate?.serviceIds ?? [];
    const next = ids.includes(serviceId)
        ? ids.filter((id) => id !== serviceId)
        : [...ids, serviceId];
    return applyFlatScopeChoice(current, 'explicit', next);
};

/**
 * Was passiert mit der Deckung, wenn die EINHEIT der Position wechselt?
 *
 * Verlaesst die Position die Monatseinheit, faellt die Pauschale weg (das
 * Feldpaar wird zurueckgesetzt): eine Pauschale ohne Monatseinheit bekaeme
 * nie einen Monatsvorschlag, ihre Deckung legte aber weiter Geld still —
 * ein stummer Dauerzustand, den niemand mehr sieht, weil das Deckungs-Select
 * mit der Einheit aus der Oberflaeche verschwindet. Der Wechsel ZUR
 * Monatseinheit erzeugt dagegen NICHTS (kein Automatismus): null heisst
 * "keine Aenderung noetig".
 */
export const flatPatchForUnitChange = (
    current: { assignment?: ServiceAssignment; flatRate?: FlatRateCoverage },
    newUnit: string,
): FlatSetupPatch | null => {
    if (isMonthlyUnit(newUnit)) return null;
    if (current.assignment !== 'flatrate' && !current.flatRate) return null;
    return applyFlatScopeChoice(current, '');
};

/**
 * Was passiert mit der Deckung, wenn die ZUORDNUNG (assignment) der Position
 * wechselt?
 *
 * Das Funktions-Select laesst den Weg ZU 'flatrate' nicht zu (Option
 * gesperrt), wohl aber den Weg WEG davon. Dann muss die Deckung MIT
 * abgeraeumt werden: massgeblich fuer das Geld ist flatRate, nicht das
 * assignment (isFlatRateService) — bliebe sie stehen, deckte eine fortan als
 * 'Begehung' angezeigte Position unsichtbar weiter und legte fremde
 * Geldstaende still. Dasselbe stumme Weiterdecken, gegen das schon
 * flatPatchForUnitChange gebaut ist. Anders als die leere Scope-Auswahl
 * (Rueckfall auf 'none') behaelt der Wechsel die NEUE Zuordnung: der
 * Bearbeiter hat sie gerade ausdruecklich gewaehlt. null heisst "keine
 * Aenderung noetig".
 */
export const flatPatchForAssignmentChange = (
    current: { assignment?: ServiceAssignment; flatRate?: FlatRateCoverage },
    newAssignment: ServiceAssignment,
): FlatSetupPatch | null => {
    if (newAssignment === 'flatrate') return null;
    if (!current.flatRate) return null;
    return { assignment: newAssignment, flatRate: undefined };
};

/**
 * Deckung, wie sie den PROJEKTKONTEXT verlassen darf (Vorlage speichern,
 * Katalogeintrag aus einer Projektposition): 'explicit'-Ids zeigen auf
 * Positionen genau dieses Projekts und waeren woanders Phantome — der Umfang
 * faellt deshalb weg. Das assignment 'flatrate' bleibt bewusst stehen: im
 * naechsten Projekt meldet flatRateSetupIssues "Deckungsumfang fehlt" und
 * erzwingt eine neue ausdrueckliche Entscheidung, statt dass die Pauschale
 * still ohne Deckung mitlaeuft.
 */
export const portableFlatRate = (
    flatRate: FlatRateCoverage | undefined,
): FlatRateCoverage | undefined => {
    if (!flatRate) return undefined;
    if (flatRate.scope === 'explicit') return undefined;
    if (flatRate.scope !== 'inspection' && flatRate.scope !== 'all') return undefined;
    return { scope: flatRate.scope };
};

export interface FlatSetupIssue {
    /** Id der Pauschalposition, um die es geht. */
    serviceId: string;
    /** 'error' = unvollstaendig, so darf kein Projekt entstehen. 'warning' = auffaellig, aber erlaubt. */
    level: 'error' | 'warning';
    message: string;
}

/** Pauschale im weiten Sinn: auch die halb angelegte (assignment ohne Deckung). */
const isFlatLike = (s: FlatSetupServiceLike): boolean =>
    isFlatRateService(s) || s.assignment === 'flatrate';

const nameOf = (s: FlatSetupServiceLike): string =>
    (s.title || '').trim() || (s.lvPosition || '').trim() || s.id;

/**
 * Prueft die Pauschal-Anlage eines LVs. Errors sind UNVOLLSTAENDIGE Eingaben
 * (halbe Pauschale, 'explicit' ohne vorhandene Ids) — mit ihnen entsteht
 * genau die wirkungslose Pauschale, gegen die diese Runde gebaut wird.
 * Warnings sind erlaubte, aber auffaellige Lagen (Umfang trifft nichts,
 * Einheit passt nicht, zwei Pauschalen ueberschneiden sich): der Bearbeiter
 * soll sie sehen und entscheiden, nicht die Maschine.
 *
 * ZWEI PAUSCHALEN im selben Projekt sind KEIN Fehler: beide bleiben
 * forderbar, keine deckt die andere (Regel in resolveFlatCoverage). Gemeldet
 * wird nur die Ueberschneidung — wenn dieselbe Position von mehreren
 * Pauschalen erfasst waere, deckt deterministisch die erste, und das soll
 * eine bewusste Entscheidung sein, kein Zufall der LV-Reihenfolge.
 */
export const flatRateSetupIssues = (
    services: FlatSetupServiceLike[] | null | undefined,
): FlatSetupIssue[] => {
    const list = Array.isArray(services) ? services : [];
    const issues: FlatSetupIssue[] = [];
    const flats = list.filter(isFlatLike);
    if (flats.length === 0) return issues;
    const coverables = list.filter((s) => !isFlatLike(s));

    flats.forEach((flat) => {
        const name = nameOf(flat);
        if (!isFlatRateService(flat)) {
            issues.push({
                serviceId: flat.id,
                level: 'error',
                message: `„${name}“ ist als Monatspauschale markiert, aber ohne Deckungsumfang — sie deckt so keine einzige Position.`,
            });
            return;
        }
        const rate = flat.flatRate!;
        if (!isMonthlyUnit(flat.unit)) {
            issues.push({
                serviceId: flat.id,
                level: 'warning',
                message: `„${name}“ traegt eine Deckung, aber keine Monatseinheit — ohne Einheit 'Monat' gibt es keinen Monatsvorschlag.`,
            });
        }
        if (rate.scope === 'explicit') {
            const known = new Set(coverables.map((s) => s.id));
            const valid = (rate.serviceIds ?? []).filter((id) => known.has(id));
            if (valid.length === 0) {
                issues.push({
                    serviceId: flat.id,
                    level: 'error',
                    message: `„${name}“ soll einzelne Positionen decken, aber es ist keine (vorhandene) Position gewaehlt.`,
                });
            }
        } else if (rate.scope === 'inspection') {
            // Dieselbe Frage wie resolveFlatCoverage, ueber dieselbe Regel
            // beantwortet: die Aufloesung NUR mit dieser Pauschale zeigt, ob
            // ihr Umfang ueberhaupt etwas trifft. Keine zweite
            // Begehungsdefinition neben flatRateLogic.
            const hits = resolveFlatCoverage([flat, ...coverables]).size;
            if (hits === 0) {
                issues.push({
                    serviceId: flat.id,
                    level: 'warning',
                    message: `„${name}“ deckt nur Begehungen, aber das LV enthaelt keine Begehungsposition.`,
                });
            }
        } else if (coverables.length === 0) {
            issues.push({
                serviceId: flat.id,
                level: 'warning',
                message: `„${name}“ soll alle Positionen decken, aber es gibt keine Position, die sie decken koennte.`,
            });
        }
    });

    // Ueberschneidung: dieselbe Position von mehreren Pauschalen erfasst.
    // Je Pauschale einzeln aufgeloest (nur sie + die deckbaren Positionen),
    // damit die Gewinner-Regel der Gesamtaufloesung die zweite Deckung nicht
    // verbirgt.
    const activeFlats = flats.filter(isFlatRateService);
    if (activeFlats.length > 1) {
        const coveredBy = new Map<string, string[]>();
        activeFlats.forEach((flat) => {
            resolveFlatCoverage([flat, ...coverables]).forEach((_winner, coveredId) => {
                coveredBy.set(coveredId, [...(coveredBy.get(coveredId) ?? []), flat.id]);
            });
        });
        coveredBy.forEach((flatIds, coveredId) => {
            if (flatIds.length < 2) return;
            const covered = list.find((s) => s.id === coveredId);
            issues.push({
                serviceId: flatIds[0],
                level: 'warning',
                message: `„${covered ? nameOf(covered) : coveredId}“ waere von mehreren Pauschalen erfasst — es deckt die erste in LV-Reihenfolge.`,
            });
        });
    }

    return issues;
};
